import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5jtdh9ugl990ce158o0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-16 bg-gradient-to-br from-teal-50 via-purple-50 to-rose-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-purple-500 text-white px-4 py-2 rounded-full mb-4 shadow-lg text-sm">
              <i className="ri-mail-line text-base"></i>
              <span className="font-semibold whitespace-nowrap">Get in Touch</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-teal-600 to-purple-600 bg-clip-text text-transparent">
              Contact L.J. Boydston 💌
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Have questions about the books? Want to arrange a school visit or book reading? 
              I'd love to hear from you!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-6 transform hover:scale-[1.02] transition-all duration-300">
              <h3 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                <i className="ri-message-3-line text-teal-600 text-xl"></i>
                Send a Message
              </h3>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors resize-none text-sm"
                    placeholder="Your message here... (max 500 characters)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-purple-500 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again later.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Email Card */}
              <div className="bg-white rounded-3xl shadow-xl p-6 transform hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      For general inquiries and book-related questions
                    </p>
                    <a
                      href="mailto:info@leavittpeakpress.com"
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                    >
                      info@leavittpeakpress.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Amazon Store Card */}
              <div className="bg-white rounded-3xl shadow-xl p-6 transform hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Amazon Author Page</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Visit my Amazon store to see all available books
                    </p>
                    <a
                      href="https://www.amazon.com/stores/L-J-Boydston/author/B0GFBQXQXQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      View Amazon Store
                      <i className="ri-external-link-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* School Visits Card */}
              <div className="bg-gradient-to-br from-amber-400 to-rose-500 rounded-3xl shadow-xl p-6 text-white transform hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-school-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold mb-1">School Visits & Readings</h4>
                    <p className="text-sm mb-2 opacity-90">
                      Interested in having L.J. visit your school or library? 
                      Send a message to discuss availability and details!
                    </p>
                    <div className="flex items-center gap-1 text-sm font-semibold">
                      <i className="ri-calendar-check-line"></i>
                      <span>Now Booking for 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
